// 处理"新版本部署后旧 chunk hash 失效"导致的懒加载失败。
//
// 背景：业务路由用 React.lazy(() => import(...)) 拆成 hash chunk。发新版时服务器上
// 旧 hash 文件被替换，但用户浏览器里仍在跑旧的主 bundle（index-*.js 被 nginx 缓存
// 30 天），它引用的是旧 chunk hash → 点进 procurement 等懒加载页时请求旧文件 404 →
// "Failed to fetch dynamically imported module" → 掉进 React Router 默认 ErrorBoundary。
// 用户手动刷新就好，因为刷新会重新拉最新 index.html → 最新 bundle → 正确 chunk hash。
//
// 这里把"手动刷新"自动化：Vite 在动态 import 预加载失败时会在 window 派发
// 'vite:preloadError'，我们监听后自动刷新一次。用 sessionStorage 记时间戳做守卫，
// 避免"文件真的丢了（刷新也 404）"时陷入无限刷新循环，同时又允许会话后期的另一次
// 新部署再触发一次刷新。

const RELOAD_GUARD_KEY = 'nova:chunk-reload-at'
// 冷却窗口：一次自动刷新后 15s 内再次 chunk 失败，视为"刷新也没救回来"，
// 不再刷新，让错误正常抛给 ErrorBoundary。正常的部署失效场景，刷新一次即恢复。
const RELOAD_COOLDOWN_MS = 15_000

// 动态 import 失败时浏览器抛出的 message 特征（各内核措辞不同，取交集关键词）。
const DYNAMIC_IMPORT_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i

/**
 * 判断"现在"是否应该自动刷新。纯函数，便于单测。
 * @param now       当前时间戳（ms）
 * @param lastReload 上次自动刷新的时间戳字符串（来自 sessionStorage），无则 null
 * @returns 是否应刷新
 */
export function shouldReload(now: number, lastReload: string | null): boolean {
  if (!lastReload) return true
  const last = Number(lastReload)
  if (!Number.isFinite(last)) return true
  // 距上次自动刷新已超过冷却窗口 → 认为是新一次失效，允许再刷
  return now - last > RELOAD_COOLDOWN_MS
}

/**
 * 带冷却守卫地刷新一次页面。除了 chunk 失效，i18n 懒加载翻译包失败（同样是旧 hash 404）也走这里。
 */
export function reloadOnce(): void {
  const now = Date.now()
  const last = sessionStorage.getItem(RELOAD_GUARD_KEY)
  if (!shouldReload(now, last)) {
    // 刚刷新过仍失败 → 不再刷，交给 ErrorBoundary 显示，避免死循环
    console.error('[chunkReload] chunk still failing after reload, giving up to avoid reload loop')
    return
  }
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(now))
  console.warn('[chunkReload] dynamic import failed (stale deploy chunk), reloading page')
  window.location.reload()
}

/**
 * 安装 chunk 加载失败的自动刷新兜底。应在应用入口尽早调用一次。
 */
export function installChunkReloadHandler(): void {
  // 1) Vite 官方推荐：预加载失败事件
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault() // 阻止 Vite 默认再次 throw
    reloadOnce()
  })

  // 2) 兜底：部分路径/内核不走 preloadError，动态 import 的 reject 会冒泡成
  //    unhandledrejection。仅匹配动态 import 的 message，避免误伤普通网络请求失败。
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message = typeof reason === 'string' ? reason : reason?.message
    if (message && DYNAMIC_IMPORT_ERROR_RE.test(message)) {
      reloadOnce()
    }
  })
}
